import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Copy, Hash, Package, Plus, Search, Upload } from "lucide-react";
import { formatMoney } from "@finora/config";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

type ImportFormat = "csv" | "minimax";

function ImportCsvModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [format, setFormat] = useState<ImportFormat>("csv");
  const [file, setFile] = useState<File | null>(null);
  const [priceIncludesVat, setPriceIncludesVat] = useState(true);
  const [taxRateId, setTaxRateId] = useState("");
  const [result, setResult] = useState<{
    created: number;
    updated?: number;
    skipped: number;
    errors: string[];
  } | null>(null);

  const { data: taxRates } = useQuery({ queryKey: ["tax-rates"], queryFn: () => api.listTaxRates() });

  const importMutation = useMutation({
    mutationFn: () =>
      format === "minimax"
        ? api.importProducts("minimax", file!)
        : api.importProductsCsv(file!, priceIncludesVat, taxRateId),
    onSuccess: (res) => {
      setResult(res);
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });

  function switchFormat(next: ImportFormat) {
    setFormat(next);
    setFile(null);
    setResult(null);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-surface p-6 shadow-xl">
        <h2 className="mb-1 text-lg font-semibold text-slate-900">Uvoz artikala</h2>

        <div className="mb-4 flex gap-1 border-b border-border">
          <button
            onClick={() => switchFormat("csv")}
            className={`border-b-2 px-3 py-2 text-sm font-medium ${
              format === "csv" ? "border-brand text-brand" : "border-transparent text-slate-500"
            }`}
          >
            WooCommerce CSV
          </button>
          <button
            onClick={() => switchFormat("minimax")}
            className={`border-b-2 px-3 py-2 text-sm font-medium ${
              format === "minimax" ? "border-brand text-brand" : "border-transparent text-slate-500"
            }`}
          >
            Minimax Excel
          </button>
        </div>

        {format === "csv" ? (
          <p className="mb-4 text-sm text-slate-500">
            Radi sa WooCommerce izvozom - prepoznaje kolone "Name"/"Naziv", "Price"/"Regular price" i
            opciono "Cost"/"Nabavna cena".
          </p>
        ) : (
          <p className="mb-4 text-sm text-slate-500">
            Radi sa MiniMax "Šifarnik artikala" Excel exportom (kolone: Identifikator artikla, Šifra,
            Naziv, Tip, JM, %PDV, Cena). Ponovni uvoz istog fajla ažurira postojeće artikle umesto da
            pravi duplikate.
          </p>
        )}

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              {format === "csv" ? "CSV fajl" : "Excel fajl (.xlsx)"}
            </label>
            <input
              type="file"
              accept={format === "csv" ? ".csv" : ".xlsx,.xls"}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm"
            />
          </div>

          {format === "csv" && (
            <>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Podrazumevana poreska stopa
                </label>
                <select
                  value={taxRateId}
                  onChange={(e) => setTaxRateId(e.target.value)}
                  className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                >
                  <option value="">Izaberi stopu...</option>
                  {taxRates?.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.isVatExempt ? t.name : `${t.ratePercent}%`}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setPriceIncludesVat(true)}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm ${
                    priceIncludesVat ? "border-brand bg-brand-light text-brand" : "border-border text-slate-600"
                  }`}
                >
                  Cena u fajlu SADRŽI PDV
                </button>
                <button
                  onClick={() => setPriceIncludesVat(false)}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm ${
                    !priceIncludesVat ? "border-brand bg-brand-light text-brand" : "border-border text-slate-600"
                  }`}
                >
                  Cena NE sadrži PDV
                </button>
              </div>
            </>
          )}

          {result && (
            <div className="rounded-lg bg-app-bg p-3 text-sm">
              <p>
                Uvezeno: <strong>{result.created}</strong>
                {result.updated !== undefined && (
                  <>
                    {" "}
                    · Ažurirano: <strong>{result.updated}</strong>
                  </>
                )}
                {" "}
                · Preskočeno: <strong>{result.skipped}</strong>
              </p>
              {result.errors.length > 0 && (
                <ul className="mt-1 list-disc pl-4 text-xs text-red-600">
                  {result.errors.slice(0, 10).map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {importMutation.isError && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">Uvoz nije uspeo.</p>
          )}
        </div>

        <div className="mt-5 flex gap-3">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            Zatvori
          </Button>
          <Button
            className="flex-1"
            disabled={!file || (format === "csv" && !taxRateId) || importMutation.isPending}
            onClick={() => importMutation.mutate()}
          >
            {importMutation.isPending ? "Uvozim..." : "Uvezi"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CodesOverview() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["product-codes"], queryFn: () => api.listProductCodes() });
  const generate = useMutation({
    mutationFn: () => api.generateProductCodes(),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ["product-codes"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      window.alert(`Gotovo. Dodeljene šifre za ${r.updated} od ${r.total} artikala.`);
    },
  });
  const rows = (data ?? []).filter((p) => !filter || `${p.sku} ${p.name} ${p.barcode ?? ""}`.toLowerCase().includes(filter.toLowerCase()));
  const sorted = [...rows].sort((a, b) => Number(a.sku) - Number(b.sku) || a.name.localeCompare(b.name));
  const copyAll = () => {
    const text = sorted.map((p) => `${p.sku}\t${p.name}`).join("\n");
    navigator.clipboard?.writeText(text);
  };
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">
        Šifre artikala koje treba uneti u Minimax (polje „Šifra“). Sve dalje mapiranje i uvoz računa iz Poreske oslanja se na ove šifre.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter po šifri ili nazivu"
          className="w-64 rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:border-brand focus:outline-none"
        />
        <Button
          variant="secondary"
          onClick={() => {
            if (window.confirm("Generisati šifre (1, 2, 3...) artiklima koji nemaju kratku brojčanu šifru?")) generate.mutate();
          }}
          disabled={generate.isPending}
        >
          <Hash className="h-4 w-4" aria-hidden />
          Generiši šifre
        </Button>
        <Button variant="secondary" onClick={copyAll}>
          <Copy className="h-4 w-4" aria-hidden />
          Kopiraj spisak
        </Button>
      </div>
      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="p-5"><Skeleton className="h-10 w-full" /></div>
        ) : sorted.length === 0 ? (
          <div className="p-5"><EmptyState icon={Package} title="Nema artikala" description="Prvo uvezite artikle." /></div>
        ) : (
          <table>
            <thead>
              <tr><th>Šifra</th><th>Naziv</th><th>Barkod</th><th>Cena (sa PDV)</th></tr>
            </thead>
            <tbody>
              {sorted.map((p) => (
                <tr key={p.id}>
                  <td className="font-mono font-medium text-slate-800">{p.sku}</td>
                  <td>{p.name}</td>
                  <td className="text-slate-500">{p.barcode ?? ""}</td>
                  <td>{formatMoney(p.unitPriceGross)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function ProductsPage() {
  const [tab, setTab] = useState<"lista" | "sifre">("lista");
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["products", search],
    queryFn: () => api.listProducts({ search: search || undefined, ingredients: "include" }),
  });

  const archive = useMutation({
    mutationFn: (id: string) => api.archiveProduct(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["products"] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Artikli i usluge</h1>
          <p className="text-sm text-slate-500">Katalog proizvoda i usluga za fakturisanje i POS</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4" aria-hidden />
            Uvezi artikle
          </Button>
          <Button onClick={() => navigate("/artikli/novi")}>
            <Plus className="h-4 w-4" aria-hidden />
            Novi artikal
          </Button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-border">
        {([["lista", "Artikli"], ["sifre", "Pregled šifara"]] as const).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm font-medium ${tab === k ? "border-b-2 border-brand text-brand" : "text-slate-500"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "sifre" ? <CodesOverview /> : (<>
      <label className="relative block max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Pretraži po nazivu, šifri ili barkodu"
          className="w-full rounded-lg border border-border bg-surface py-2 pl-9 pr-3 text-sm focus:border-brand focus:outline-none"
        />
      </label>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !data || data.length === 0 ? (
          <div className="p-5">
            <EmptyState icon={Package} title="Nema artikala" description="Dodajte prvi artikal da bi se pojavio ovde i na POS ekranu." />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th />
                <th>Naziv</th>
                <th>Šifra</th>
                <th>Tip</th>
                <th>JM</th>
                <th>Poreska stopa</th>
                <th>Cena (sa PDV)</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.map((product) => (
                <tr key={product.id} className="cursor-pointer hover:bg-slate-50">
                  <td onClick={() => navigate(`/artikli/${product.id}/izmeni`)}>
                    {product.imageUrl ? (
                      <img src={product.imageUrl} alt="" className="h-8 w-8 rounded object-cover" />
                    ) : (
                      <div className="h-8 w-8 rounded bg-app-bg" />
                    )}
                  </td>
                  <td className="font-medium text-slate-800" onClick={() => navigate(`/artikli/${product.id}/izmeni`)}>
                    {product.name}
                  </td>
                  <td className="text-slate-500" onClick={() => navigate(`/artikli/${product.id}/izmeni`)}>
                    {product.sku}
                  </td>
                  <td className="text-slate-500" onClick={() => navigate(`/artikli/${product.id}/izmeni`)}>
                    {product.isService ? "Usluga" : "Roba"}
                  </td>
                  <td className="text-slate-500" onClick={() => navigate(`/artikli/${product.id}/izmeni`)}>
                    {product.unit}
                  </td>
                  <td className="text-slate-500" onClick={() => navigate(`/artikli/${product.id}/izmeni`)}>
                    {product.taxRate?.isVatExempt ? product.taxRate.name : `${product.taxRate?.ratePercent}%`}
                  </td>
                  <td onClick={() => navigate(`/artikli/${product.id}/izmeni`)}>{formatMoney(product.unitPriceGross)}</td>
                  <td>
                    <button
                      aria-label="Obriši artikal"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Obrisati artikal "${product.name}"?`)) {
                          archive.mutate(product.id);
                        }
                      }}
                      className="text-slate-400 hover:text-red-600"
                    >
                      <Archive className="h-4 w-4" aria-hidden />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      </>)}

      {importOpen && <ImportCsvModal onClose={() => setImportOpen(false)} />}
    </div>
  );
}
